import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";
import {
  CheckCircle,
  Loader2,
  Smile,
  Frown,
  Meh,
  Sparkles,
  Wand2,
  Copy,
  Check,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { isToday } from "date-fns";
import type { AISuggestion } from "@dmtrigger/shared";

// Mood labels generator based on mood score (1-10)
const generateMoodLabels = (moodScore: number): string[] => {
  const allKeywords: Record<number, string[]> = {
    1: ["devastated", "hopeless", "crushed", "desperate", "overwhelmed", "broken"],
    2: ["terrible", "awful", "miserable", "depressed", "anxious", "lost"],
    3: ["sad", "down", "unhappy", "discouraged", "worried", "tired"],
    4: ["disappointed", "frustrated", "concerned", "uneasy", "drained", "low"],
    5: ["neutral", "okay", "balanced", "steady", "content", "fine"],
    6: ["good", "positive", "hopeful", "comfortable", "relaxed", "pleased"],
    7: ["happy", "cheerful", "optimistic", "enthusiastic", "content", "joyful"],
    8: ["excited", "thrilled", "delighted", "ecstatic", "energized", "passionate"],
    9: ["amazing", "wonderful", "fantastic", "incredible", "blissful", "radiant"],
    10: ["perfect", "euphoric", "transcendent", "unbelievable", "supreme", "divine"],
  };

  return allKeywords[moodScore] || allKeywords[5];
};

export function CheckIn() {
  const [message, setMessage] = useState("");
  const [moodScore, setMoodScore] = useState<number | undefined>(undefined);
  const [selectedMoodLabels, setSelectedMoodLabels] = useState<string[]>([]);
  const [hasInteractedWithMood, setHasInteractedWithMood] = useState(false);
  const [hasStartedEditingMessage, setHasStartedEditingMessage] = useState(false);

  // AI suggestions state
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [copiedSuggestionId, setCopiedSuggestionId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const apiClient = useApi();

  // Fetch user status to check if they've checked in today
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["status"],
    queryFn: () => apiClient.getStatus(),
  });

  const hasCheckedInToday =
    status && status.lastCheckin ? isToday(new Date(status.lastCheckin)) : false;

  // Pre-populate message and mood fields when status loads and user has checked in today
  useEffect(() => {
    if (status && hasCheckedInToday && !hasStartedEditingMessage) {
      if (status.lastCheckinMessage) {
        setMessage(status.lastCheckinMessage);
      }
      // Pre-populate mood data from last check-in metadata if available
      if (status.lastCheckinMoodScore) {
        setMoodScore(status.lastCheckinMoodScore);
        setHasInteractedWithMood(true);
      }
      if (status.lastCheckinMoodLabels && status.lastCheckinMoodLabels.length > 0) {
        setSelectedMoodLabels(status.lastCheckinMoodLabels);
        setHasInteractedWithMood(true);
      }
    }
  }, [status, hasCheckedInToday, hasStartedEditingMessage]);

  // Generate mood labels based on current mood score
  const moodLabels = moodScore ? generateMoodLabels(moodScore) : [];

  const checkInMutation = useMutation({
    mutationFn: () =>
      apiClient.checkIn({
        message: message.trim() || undefined,
        ...(hasInteractedWithMood && moodScore && { moodScore }),
        ...(hasInteractedWithMood &&
          selectedMoodLabels.length > 0 && { moodLabels: selectedMoodLabels }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["status"] });
      setMessage("");
      setSelectedMoodLabels([]);
      // Don't reset moodScore as user might want to keep similar mood
    },
  });

  // Mutation for updating check-in message and mood
  const updateMessageMutation = useMutation({
    mutationFn: (newMessage: string) =>
      apiClient.post("/api/v1/checkin/update-message", {
        message: newMessage.trim() || undefined,
        ...(hasInteractedWithMood && moodScore && { moodScore }),
        ...(hasInteractedWithMood &&
          selectedMoodLabels.length > 0 && { moodLabels: selectedMoodLabels }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["status"] });
      setSelectedMoodLabels([]);
    },
  });

  // AI suggestions mutation
  const aiSuggestionsMutation = useMutation({
    mutationFn: (data: { moodScore?: number; moodLabels?: string[]; message?: string }) =>
      apiClient.post("/api/v1/ai/suggestions", data),
    onSuccess: (response: any) => {
      setAiSuggestions(response.data?.suggestions || []);
      setShowAiSuggestions(true);
    },
  });

  // Generate AI suggestions when user has mood data
  const generateAiSuggestions = () => {
    const data: { moodScore?: number; moodLabels?: string[]; message?: string } = {};

    if (moodScore) {
      data.moodScore = moodScore;
    }

    if (selectedMoodLabels.length > 0) {
      data.moodLabels = selectedMoodLabels;
    }

    if (message.trim()) {
      data.message = message.trim();
    }

    aiSuggestionsMutation.mutate(data);
  };

  // Copy suggestion to clipboard
  const copySuggestion = async (text: string, suggestionId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSuggestionId(suggestionId);
      setTimeout(() => setCopiedSuggestionId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  // Use suggestion as message
  const applySuggestion = (text: string) => {
    setMessage(text);
    setShowAiSuggestions(false);
  };

  const handleMoodScoreChange = (value: number) => {
    setMoodScore(value);
    setSelectedMoodLabels([]); // Clear selected labels when mood score changes
    setHasInteractedWithMood(true);
  };

  const handleLabelSelect = (label: string) => {
    if (selectedMoodLabels.includes(label)) {
      setSelectedMoodLabels((prev) => prev.filter((l) => l !== label));
    } else {
      setSelectedMoodLabels((prev) => [...prev, label]);
    }
    setHasInteractedWithMood(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (hasCheckedInToday) {
      // Update existing check-in message
      updateMessageMutation.mutate(message);
    } else {
      // Create new check-in
      checkInMutation.mutate();
    }
  };

  if (statusLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Check In"
          description="Confirm you're alive and well"
          icon={CheckCircle}
        />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Check In" description="Confirm you're alive and well" icon={CheckCircle} />

      {/* Show notification if already checked in today */}
      {hasCheckedInToday && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Already Checked In Today</h3>
              <p className="text-sm text-green-700">You can update your message below if needed.</p>
            </div>
          </div>
        </div>
      )}

      {/* Always show check-in form */}
      <div className="bg-white py-6 sm:py-8 px-4 sm:px-6 shadow rounded-lg sm:px-10">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Mood Level Slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                How are you feeling today? (optional)
              </label>
              {hasInteractedWithMood && (
                <button
                  type="button"
                  onClick={() => {
                    setMoodScore(undefined);
                    setSelectedMoodLabels([]);
                    setHasInteractedWithMood(false);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear mood
                </button>
              )}
            </div>
            {hasInteractedWithMood && moodScore ? (
              <>
                <div className="flex items-center space-x-4">
                  <Frown className="h-5 w-5 text-red-500" />
                  <div className="flex-1">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={moodScore}
                      onChange={(e) => handleMoodScoreChange(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1</span>
                      <span>5</span>
                      <span>10</span>
                    </div>
                  </div>
                  <Smile className="h-5 w-5 text-green-500" />
                </div>
                <div className="flex items-center justify-center mt-2">
                  <span className="text-sm font-medium text-gray-700">
                    Mood Score: {moodScore}/10
                  </span>
                  {moodScore <= 3 && <Frown className="h-4 w-4 text-red-500 ml-2" />}
                  {moodScore >= 4 && moodScore <= 6 && (
                    <Meh className="h-4 w-4 text-yellow-500 ml-2" />
                  )}
                  {moodScore >= 7 && <Smile className="h-4 w-4 text-green-500 ml-2" />}
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => handleMoodScoreChange(5)}
                className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
              >
                💭 Add mood tracking (optional)
              </button>
            )}
          </div>

          {/* Mood Labels Selector */}
          {hasInteractedWithMood && moodScore && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select mood labels
              </label>
              <div className="flex flex-wrap gap-2">
                {moodLabels.map((label) => {
                  const isSelected = selectedMoodLabels.includes(label);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => handleLabelSelect(label)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-red-100 text-red-800 border border-red-300"
                          : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {selectedMoodLabels.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  Selected: {selectedMoodLabels.join(", ")}
                </div>
              )}
            </div>
          )}

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700">
              Additional Message
            </label>
            <div className="mt-1">
              <textarea
                id="message"
                name="message"
                rows={4}
                className="shadow-sm focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="How are you feeling today? Any notes about your well-being..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setHasStartedEditingMessage(true);
                }}
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              This message will be stored with your check-in for your records.
            </p>
          </div>

          {/* AI Suggestions Button */}
          {(moodScore || selectedMoodLabels.length > 0 || message.trim()) && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={generateAiSuggestions}
                disabled={aiSuggestionsMutation.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                {aiSuggestionsMutation.isPending ? "Generating..." : "Get AI Suggestions"}
              </button>
            </div>
          )}

          {/* AI Suggestions */}
          {showAiSuggestions && aiSuggestions.length > 0 && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Sparkles className="h-5 w-5 text-indigo-600 mr-2" />
                <h3 className="text-sm font-medium text-indigo-900">AI Message Suggestions</h3>
              </div>

              <div className="space-y-3">
                {aiSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.id}
                    className="bg-white border border-indigo-100 rounded-md p-3"
                  >
                    <p className="text-sm text-gray-800 mb-2">"{suggestion.text}"</p>
                    <p className="text-xs text-gray-600 italic mb-3">{suggestion.reasoning}</p>

                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => applySuggestion(suggestion.text)}
                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Use This
                      </button>
                      <button
                        type="button"
                        onClick={() => copySuggestion(suggestion.text, suggestion.id)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        {copiedSuggestionId === suggestion.id ? (
                          <Check className="h-3 w-3 mr-1" />
                        ) : (
                          <Copy className="h-3 w-3 mr-1" />
                        )}
                        {copiedSuggestionId === suggestion.id ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAiSuggestions(false)}
                  className="text-xs text-indigo-600 hover:text-indigo-800"
                >
                  Hide suggestions
                </button>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={checkInMutation.isPending}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkInMutation.isPending ? (
                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
              ) : (
                <CheckCircle className="-ml-1 mr-3 h-5 w-5" />
              )}
              {checkInMutation.isPending || updateMessageMutation.isPending
                ? hasCheckedInToday
                  ? "Updating..."
                  : "Checking In..."
                : hasCheckedInToday
                  ? "Update Message"
                  : "Check In Now"}
            </button>
          </div>
        </form>

        {(checkInMutation.isSuccess || updateMessageMutation.isSuccess) && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  {checkInMutation.isSuccess ? "Check-in Successful" : "Message Updated"}
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  {checkInMutation.isSuccess ? (
                    <>
                      <p>Your check-in has been recorded successfully.</p>
                      {checkInMutation.data && (
                        <p className="mt-1">
                          Next check-in due:{" "}
                          {new Date(checkInMutation.data.nextExpectedCheckin).toLocaleString()}
                        </p>
                      )}
                    </>
                  ) : (
                    <p>Your check-in message has been updated successfully.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {(checkInMutation.isError || updateMessageMutation.isError) && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {checkInMutation.isError ? "Check-in Failed" : "Update Failed"}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {(checkInMutation.error || updateMessageMutation.error)?.message ||
                    (checkInMutation.isError
                      ? "An error occurred while checking in."
                      : "An error occurred while updating your message.")}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
